/**
 * システム接続状態表示コンポーネント
 * バックエンド・MLモデル・データベースの接続状況を表示
 */

import React, { useState, useEffect } from 'react';

export function ConnectionStatus() {
  const [connections, setConnections] = useState({
    backend_api: { status: 'checking', last_check: null, response_time: null },
    ml_model: { status: 'checking', last_check: null, version: null },
    simulation: { status: 'checking', last_check: null, return_rate: null },
    race_data: { status: 'checking', last_check: null, records: null }
  });

  const checkConnections = async () => {
    const startTime = Date.now();
    
    try {
      // 1. バックエンドAPI接続確認
      const API_BASE = import.meta.env.VITE_API_URL || 'https://keiba-prediction-api.riho-dare.workers.dev';
      console.log('🔍 ConnectionStatus: API_BASE =', API_BASE);
      
      const apiResponse = await fetch(`${API_BASE}/health`);
      const apiEndTime = Date.now();
      
      if (apiResponse.ok) {
        const apiData = await apiResponse.json();
        setConnections(prev => ({
          ...prev,
          backend_api: {
            status: 'connected',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            response_time: `${apiEndTime - startTime}ms`
          }
        }));
      } else {
        setConnections(prev => ({
          ...prev,
          backend_api: {
            status: 'error',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            response_time: null
          }
        }));
      }

      // 2. MLモデル接続確認
      const modelResponse = await fetch(`${API_BASE}/api/model/latest`);
      if (modelResponse.ok) {
        const modelData = await modelResponse.json();
        setConnections(prev => ({
          ...prev,
          ml_model: {
            status: 'connected',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            version: modelData.version || modelData.fallback?.version || 'Unknown'
          }
        }));
      } else {
        setConnections(prev => ({
          ...prev,
          ml_model: {
            status: 'error',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            version: null
          }
        }));
      }

      // 3. シミュレーション結果確認
      const simResponse = await fetch(`${API_BASE}/api/simulation/current`);
      if (simResponse.ok) {
        const simData = await simResponse.json();
        setConnections(prev => ({
          ...prev,
          simulation: {
            status: 'connected',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            return_rate: simData.return_rate ? `${(simData.return_rate * 100).toFixed(2)}%` : 'N/A'
          }
        }));
      } else {
        setConnections(prev => ({
          ...prev,
          simulation: {
            status: 'error',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            return_rate: null
          }
        }));
      }

      // 4. レースデータAPI確認
      const raceResponse = await fetch(`${API_BASE}/api/races/today`);
      if (raceResponse.ok) {
        const raceData = await raceResponse.json();
        setConnections(prev => ({
          ...prev,
          race_data: {
            status: 'connected',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            records: raceData.races ? `${raceData.races.length}レース` : 'レースデータ生成済み'
          }
        }));
      } else {
        setConnections(prev => ({
          ...prev,
          race_data: {
            status: 'error',
            last_check: new Date().toLocaleTimeString('ja-JP'),
            records: null
          }
        }));
      }

    } catch (error) {
      console.error('接続確認エラー:', error);
      // すべてをエラー状態に設定
      setConnections(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(key => {
          updated[key] = {
            ...updated[key],
            status: 'error',
            last_check: new Date().toLocaleTimeString('ja-JP')
          };
        });
        return updated;
      });
    }
  };

  useEffect(() => {
    // 初回チェック
    checkConnections();
    
    // 30秒ごとに再チェック
    const interval = setInterval(checkConnections, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'connected': return '✅';
      case 'error': return '❌';
      case 'checking': return '🔄';
      default: return '❓';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'connected': return 'text-green-600';
      case 'error': return 'text-red-600';
      case 'checking': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  return (
    <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-blue-900">🔗 システム接続状況</h3>
        <button 
          onClick={checkConnections}
          className="text-xs px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded transition-colors"
        >
          🔄 更新
        </button>
      </div>
      
      <div className="space-y-2 text-sm">
        {/* バックエンドAPI */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-2">
            {getStatusIcon(connections.backend_api.status)}
            <span>バックエンドAPI</span>
          </span>
          <div className="text-right">
            <div className={`font-medium ${getStatusColor(connections.backend_api.status)}`}>
              {connections.backend_api.status === 'connected' ? '正常' : 
               connections.backend_api.status === 'error' ? 'エラー' : 'チェック中'}
            </div>
            {connections.backend_api.response_time && (
              <div className="text-xs text-gray-500">
                {connections.backend_api.response_time}
              </div>
            )}
          </div>
        </div>

        {/* MLモデル */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-2">
            {getStatusIcon(connections.ml_model.status)}
            <span>MLモデル</span>
          </span>
          <div className="text-right">
            <div className={`font-medium ${getStatusColor(connections.ml_model.status)}`}>
              {connections.ml_model.status === 'connected' ? '稼働中' : 
               connections.ml_model.status === 'error' ? 'エラー' : 'チェック中'}
            </div>
            {connections.ml_model.version && (
              <div className="text-xs text-gray-500">
                {connections.ml_model.version}
              </div>
            )}
          </div>
        </div>

        {/* シミュレーション */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-2">
            {getStatusIcon(connections.simulation.status)}
            <span>シミュレーション</span>
          </span>
          <div className="text-right">
            <div className={`font-medium ${getStatusColor(connections.simulation.status)}`}>
              {connections.simulation.status === 'connected' ? '実行中' : 
               connections.simulation.status === 'error' ? 'エラー' : 'チェック中'}
            </div>
            {connections.simulation.return_rate && (
              <div className="text-xs text-gray-500">
                還元率: {connections.simulation.return_rate}
              </div>
            )}
          </div>
        </div>

        {/* レースデータAPI */}
        <div className="flex justify-between items-center">
          <span className="flex items-center gap-2">
            {getStatusIcon(connections.race_data.status)}
            <span>レースデータAPI</span>
          </span>
          <div className="text-right">
            <div className={`font-medium ${getStatusColor(connections.race_data.status)}`}>
              {connections.race_data.status === 'connected' ? '取得可能' : 
               connections.race_data.status === 'error' ? 'エラー' : 'チェック中'}
            </div>
            {connections.race_data.records && (
              <div className="text-xs text-gray-500">
                {connections.race_data.records}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 最終更新時刻 */}
      <div className="mt-3 pt-2 border-t border-blue-200">
        <div className="text-xs text-gray-500 text-center">
          最終確認: {connections.backend_api.last_check || '未確認'}
        </div>
      </div>
    </div>
  );
}